import React, { Component } from 'react'
import { i18n } from '../i18n/i18n'
import labelApprovalApi, {
    APPROVAL_TEXT_MAX_LENGTH,
    LabelApprovalData,
    LabelApprovalLabelableType,
    labelDisplaySize,
    labelSizeText
} from '../apis/labelApproval.api'
import alertService from '../services/alert.service'
import Button from '../components/Button'
import Card from '../components/Card'
import InputText from '../components/InputText'
import Modal from '../components/Modal'

interface Props {
    isOpen: boolean
    labelableType: LabelApprovalLabelableType
    labelableId: number
    labelName?: string
    snapshotUrl?: string
    labelWidth?: string | number | null
    labelHeight?: string | number | null
    labelUnit?: string | null
    /** Prefill when re-sending — usually the same approver as last time. */
    defaultApproverName?: string
    defaultApproverEmail?: string
    onClose: () => void
    onSent?: (approval: LabelApprovalData) => void
}

interface State {
    approverName: string
    approverEmail: string
    message: string
    isSending: boolean
}

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export default class LabelApprovalRequestForm extends Component<Props, State> {
    get blankState (): State {
        return {
            approverName: this.props.defaultApproverName ?? '',
            approverEmail: this.props.defaultApproverEmail ?? '',
            message: '',
            isSending: false
        }
    }

    state: State = this.blankState

    handleFieldChange = (name: string, value: string) => {
        this.setState({ [name]: value } as unknown as Pick<State, keyof State>)
    }

    handleMessageChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        this.setState({
            message: event.target.value.slice(0, APPROVAL_TEXT_MAX_LENGTH)
        })
    }

    handleClose = () => {
        if (this.state.isSending) return
        this.setState(this.blankState)
        this.props.onClose()
    }

    handleSend = () => {
        const { approverName, approverEmail, message, isSending } = this.state
        if (isSending) return

        if (!approverName.trim()) {
            return alertService.error(i18n("Please enter the approver's full name"))
        }
        if (!emailPattern.test(approverEmail.trim())) {
            return alertService.error(i18n('Please enter a valid email address'))
        }

        this.setState({ isSending: true })
        labelApprovalApi.request(
            {
                labelableType: this.props.labelableType,
                labelableId: this.props.labelableId,
                approverName: approverName.trim(),
                approverEmail: approverEmail.trim(),
                message: message.trim() || undefined
            },
            approval => {
                this.setState({ isSending: false })
                if (!approval) return
                alertService.success(i18n('Approval request sent'))
                this.setState(this.blankState)
                this.props.onClose()
                if (this.props.onSent) this.props.onSent(approval)
            }
        )
    }

    render () {
        const { message, isSending } = this.state
        const remaining = APPROVAL_TEXT_MAX_LENGTH - message.length
        const { labelWidth, labelHeight, labelUnit } = this.props
        const previewSize = labelDisplaySize(labelWidth, labelHeight, labelUnit, 260, 200)
        const sizeText = labelSizeText(labelWidth, labelHeight, labelUnit)
        return (
            <Modal
                title={i18n('Send label for approval')}
                isOpen={this.props.isOpen}
                onClose={this.handleClose}
                closeDisabled={isSending}
                className="max-w-xl">
                <Card>
                    {this.props.labelName && (
                        <p className="text-sm text-secondary mb-3">
                            {i18n('Label')}: <strong>{this.props.labelName}</strong>
                        </p>
                    )}

                    {this.props.snapshotUrl && (
                        <div className="mb-4">
                            <div className="border border-solid border-gray-200 rounded-lg flex justify-center">
                                {/* Same proportions and scale the approver will see. */}
                                <img
                                    className="rounded-lg"
                                    src={this.props.snapshotUrl}
                                    alt={this.props.labelName}
                                    style={previewSize
                                        ? { width: previewSize.width, maxWidth: '100%', height: 'auto' }
                                        : { maxWidth: 260, maxHeight: 200 }}/>
                            </div>
                            {sizeText && (
                                <p className="text-xs text-secondary mt-1">
                                    {i18n('Label size')}: {sizeText}
                                </p>
                            )}
                        </div>
                    )}

                    <InputText
                        name="approverName"
                        label={i18n('Full name')}
                        value={this.state.approverName}
                        readonly={isSending}
                        onChange={this.handleFieldChange}/>

                    <InputText
                        name="approverEmail"
                        type="email"
                        label={i18n('Email address')}
                        value={this.state.approverEmail}
                        readonly={isSending}
                        onChange={this.handleFieldChange}/>

                    <div className="mb-4">
                        <label className="block text-sm text-secondary mb-1">
                            {i18n('Message')}
                        </label>
                        {/*
                          * `input-default` pins the height to a single line
                          * (h-input), so a textarea needs it released.
                          */}
                        <textarea
                            className="input-default"
                            style={{height: 'auto', minHeight: 72, paddingTop: 8, paddingBottom: 8}}
                            rows={3}
                            maxLength={APPROVAL_TEXT_MAX_LENGTH}
                            value={message}
                            disabled={isSending}
                            onChange={this.handleMessageChange}/>
                        <p className="text-xs text-secondary mt-1">
                            {remaining} {i18n('characters remaining')}
                        </p>
                    </div>

                    <p className="text-xs text-secondary mb-4">
                        {i18n('The approver receives this label by email and can approve or reject it without a QPRO+ account.')}
                    </p>

                    <div className="flex justify-end gap-3">
                        <Button
                            text={i18n('Cancel')}
                            type="secondary"
                            disabled={isSending}
                            onClick={this.handleClose}/>
                        <Button
                            text={isSending ? i18n('Sending...') : i18n('Send')}
                            progress={isSending}
                            onClick={this.handleSend}/>
                    </div>
                </Card>
            </Modal>
        )
    }
}
